import styles from './devicesUpsertTabPage.module.css';

import addIcon from 'assets/svg/add-01.svg';
import infoIcon from 'assets/svg/icon_info-01.svg';
import attentionIcon from 'assets/svg/attention-01.svg';
import deleteIcon from 'assets/svg/delete-01.svg';

import React, { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import classNames from 'classnames';
import AddDeviceModelModal from './AddDeviceModelModal';
import Select from '../../../ui/Select/Select';
import Tooltip from '../../../ui/Tooltip/index';
import Icon from 'helpers/components/Icon';
import RegularButton from 'ui/RegularButton';
import { devicesStore } from 'store/mobx/devices.store';
import { getListUnits } from '../../../services/list.services';
import { observer } from 'mobx-react-lite';
import useAlert from 'hooks/useAlert';
import FormTextField from 'ui/formItems/FormTextField';

const DeviceModelParams = ({ units, fields = [] }) => {
  const FIELD_TYPES = {
    INTEGER: 'Целое число',
    FLOAT: 'Число с плавающей точкой',
    RANGE: 'Диапазон',
  };

  const getNameUnitById = (id) => {
    const unit = units.find((item) => item.id === id);
    return unit?.name || '';
  };

  const renderFieldContent = (field) => {
    const { id, name, type, setting } = field;
    const settingValue = JSON.parse(setting);
    const fieldType = type.name;

    const renderDefault = () => <div key={id}>{`${name}: \u00A0–`}</div>;

    const renderInteger = () => {
      if (settingValue.unit === undefined) return renderDefault();
      return <div key={id}>{`${name}: \u00A0ед.изм.(${getNameUnitById(settingValue.unit)})`}</div>;
    };

    const renderFloat = () => {
      const roundingPart = settingValue.rounding
        ? `\u00A0округление- ${settingValue.rounding};`
        : '–';
      const unitPart = settingValue.unit
        ? `\u00A0ед.изм.(${getNameUnitById(settingValue.unit)})`
        : '–';
      return <div key={id}>{`${name}: ${roundingPart}${unitPart}`}</div>;
    };

    const renderRange = () => (
      <div className={styles.contentTooltip} key={id}>
        {`${name}: \u00A0`}
        {settingValue?.map((s, index) => (
          <div className={styles.contentTooltip} key={index}>
            {s.param === 'min' && <div>{`от ${s.value ?? '–'}`}</div>}
            {s.param === 'max' && <div>{`\u00A0 до ${s.value ?? '–'}`}</div>}
          </div>
        ))}
      </div>
    );

    const fieldTypeHandlers = {
      [FIELD_TYPES.INTEGER]: renderInteger,
      [FIELD_TYPES.FLOAT]: renderFloat,
      [FIELD_TYPES.RANGE]: renderRange,
    };
    return fieldTypeHandlers[fieldType]?.() || renderDefault();
  };
  return fields.map(renderFieldContent);
};

const DevicesUpsertTabPage = observer(({ mode }) => {
  const { showAlert } = useAlert();
  const {
    setDevicesModels,
    deviceModels,
    upsertDevice,
    removeDeviceModel,
    setOperationMode,
    selectedDevice,
    updateSelectedDevice,
  } = devicesStore;

  const [device, setDevice] = React.useState(selectedDevice);
  const [addDeviceModelModal, setAddDeviceModelModal] = React.useState(false);
  const [selectedDeviceModel, setSelectedDeviceModel] = React.useState(null);
  const [units, setUnits] = useState([]);

  const form = useForm({
    defaultValues: {
      name: device?.name ?? '',
      inventory_number: device?.inventory_number ?? '',
      description: device?.description,
      id_model: device?.id_model ?? null,
      id_sensor: device?.id_sensor ?? '',
    },
  });

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    control,
  } = form;

  useEffect(() => {
    const loadUnits = async () => {
      const res = await getListUnits();
      setUnits(res);
    };
    loadUnits();
  }, []);

  useEffect(() => {
    setDevice(selectedDevice);
  }, [selectedDevice]);

  useEffect(() => {
    setDevicesModels();
  }, []);

  useEffect(() => {
    const activeTab = mode.tabs.find((tab) => tab.id === mode.activeTabId);
    setOperationMode(activeTab?.operation);
    setDevice(selectedDevice());
  }, [mode.activeTabId]);

  const cancelClick = () => {
    closeTab();
  };

  const closeTab = () => {
    updateSelectedDevice(null);
    let tabs = JSON.parse(JSON.stringify(mode.tabs));
    let activeTab = tabs.find((val) => val.id == mode.activeTabId);
    let index = tabs.indexOf(activeTab);
    tabs.splice(index, 1);

    mode.setTabs(tabs);
    if (tabs.length > 0) {
      mode.setActiveTabId(tabs[tabs.length - 1].id);
      mode.setModeByParseTab(tabs[tabs.length - 1]);
    } else {
      mode.setPath('');
      mode.setType({ mode: 'view', item: '' });
      mode.setActiveTabId(0);
    }
  };

  const validateForm = (data) => {
    updateSelectedDevice({
      id: device?.id,
      name: data.name,
      description: data.description,
      inventory_number: data.inventory_number,
      id_model: data.id_model,
      id_sensor: data?.id_sensor ? data?.id_sensor : null,
    });

    if (!data.name || !data.inventory_number || !data.id_model) {
      return false;
    }
    if (device?.id) dataWillBeChangedAlert(submitClick);
    else submitClick();
  };

  const submitClick = async () => {
    const result = await upsertDevice();
    if (!result.success) {
      errorHandler(result.error_code, result.error);
    } else {
      successUpsertDeviceAlert();
      cancelClick();
    }
  };

  const removeModelClick = async (id) => {
    sureDeleteModelAlert(async () => {
      const result = await removeDeviceModel(id);
      if (!result.success) return errorHandler(result.error_code, result.error);
      successModelDeviceDeleteAlert();
      setValue('id_model', null);
      if (id == selectedDeviceModel) {
        setSelectedDeviceModel(null);
      }
    });
  };

  const errorHandler = (code, message) => {
    showAlert(` Код № ${code}${message ? ` - ${message}` : ''}!`, 'error');
  };

  const successModelDeviceDeleteAlert = () =>
    showAlert(`Модель оборудования удалена успешно!`, 'success');

  const sureDeleteModelAlert = (callbacks) => {
    showAlert(`Вы действительно хотите удалить данную модель?`, 'sure', callbacks);
  };

  const dataWillBeChangedAlert = (callback) =>
    showAlert(`Данные типа источника будут изменены!`, 'warning', callback);

  const successUpsertDeviceAlert = () =>
    showAlert(
      selectedDevice()?.id != null
        ? `Оборудование успешно изменено!`
        : 'Оборудование добавлено успешно!',
      'success',
    );

  return (
    <div className={styles.pageContainer}>
      <div className={styles.title}>Стандартные поля</div>
      <FormProvider {...form}>
        <FormTextField className={styles.formField} name="name" label={'Наименование'} required />
        <FormTextField
          className={styles.formField}
          name="inventory_number"
          label={'Инвентарный номер'}
          required
        />

        <FormTextField className={styles.formField} name="description" label={'Описание'} />

        <FormTextField
          className={styles.formField}
          name="id_sensor"
          label={'Номер датчика'}
          type={'number'}
          min={1}
        />

        <div className={styles.title}>Модель</div>
        <div className={styles.formField}>
          <div className={styles.modelSelectContainer}>
            <div className={styles.formFieldContainer}>
              <div className={styles.label}>Выберите модель</div>
              <Controller
                control={control}
                name="id_model"
                rules={{ required: 'Модель обязательна для выбора' }}
                render={({ field }) => (
                  <Select
                    options={deviceModels}
                    value={field.value}
                    onChange={(id) => field.onChange(id)}
                    titleKey="name"
                    valueKey="id"
                    className={styles.select}
                    notSelectedText="Не выбрано"
                    renderOption={({ option, isSelected }) => (
                      <div
                        className={classNames(isSelected ? 'selected' : '', styles.optionWrapper)}
                      >
                        <Tooltip
                          styleConfig={{ tooltip: styles.tooltipContent }}
                          text={<DeviceModelParams units={units} fields={option.user_fields} />}
                        >
                          <Icon width="15px" height="15px" color="grey" src={infoIcon} />
                        </Tooltip>
                        <span className={styles.optionName}>{option.name}</span>
                        <Icon
                          width="15px"
                          height="15px"
                          color={'var(--color-brand)'}
                          src={deleteIcon}
                          onClick={() => removeModelClick(option.id)}
                        />
                      </div>
                    )}
                  />
                )}
              />
            </div>
            <RegularButton
              className={styles.regularButton}
              text="Создать новую модель"
              color="secondary"
              onClick={() => setAddDeviceModelModal(true)}
              appendIcon={
                <div className={styles.addButtonContainer}>
                  <Icon className={styles.addButton} src={addIcon} color={'#fff'} />
                </div>
              }
            />
          </div>
          {!!errors.id_model && (
            <div className={styles.dataErrorBlock}>
              <img src={attentionIcon} alt={'attention'} />
              <div className={styles.dataError}>{errors.id_model.message}</div>
            </div>
          )}
        </div>
      </FormProvider>
      <div className={styles.footer}>
        <RegularButton
          onClick={handleSubmit(validateForm)}
          text={device?.id == null ? 'Добавить' : 'Редактировать'}
          variant="flat"
        />
        <RegularButton
          onClick={() => cancelClick()}
          text="Отмена"
          variant="outlined"
          color="secondary"
        />
      </div>
      {addDeviceModelModal && (
        <AddDeviceModelModal setModalOpened={setAddDeviceModelModal} mode={mode} />
      )}
    </div>
  );
});

export default DevicesUpsertTabPage;
